import * as Sentry from '@sentry/vue';

import { pathOr } from 'ramda';
import {
  differenceInCalendarDays,
  differenceInHours,
  differenceInSeconds,
  formatDistance,
  parseISO,
} from 'date-fns';
import getFnsLocale from '~/lib/dateFnsLocale';
import {
  MINI_PLAN_NAME,
  PREMIUM_PLAN_NAME,
  EXPIRE_SUBSCRIPTION_THRESHOLD_DAYS,
} from '~/config/constants';
import posthogInstance from '../packs/posthog';

export const AUTH_STATUS_NONE = 'AUTH_STATUS_NONE';
export const AUTH_STATUS_PENDING = 'AUTH_STATUS_PENDING';
export const AUTH_STATUS_AUTHORIZED = 'AUTH_STATUS_AUTHORIZED';
export const AUTH_STATUS_ERROR = 'AUTH_STATUS_ERROR';

export const DEFAULT_HEADERS = {
  'Content-Type': 'application/json',
};

const ERROR_MESSAGES = {
  401: 'signIn.error.wrongCodeOrEmail',
};

const defaultState = () => ({
  user: {
    active_until: null,
    current_plan_name: null,
    email: null,
    last_invoice_number: null,
    states_enabled: false,
    user_hash: null,
  },
  tokens: {
    access: null,
    access_expires_at: null,
    refresh: null,
    refresh_expires_at: null,
    csrf: null,
  },
  keepMeSignedIn: false,
  authStatus: AUTH_STATUS_NONE,
  errorMessage: null,
  errorMessages: {},
  dialogOpened: false,
  dialogData: {
    step: null,
    email: null,
  },
});

export default {
  namespaced: true,
  state: defaultState(),
  mutations: {
    requestResetPasswordSuccess() {},
    clearErrorMessages(state) {
      state.errorMessages = {};
      state.errorMessage = null;
    },
    signInSuccess(state, action) {
      state.authStatus = AUTH_STATUS_AUTHORIZED;
      state.user = action.payload.data.user;
      state.errorMessage = null;

      Sentry.setUser({ email: state.user.email });

      const ph = posthogInstance || window.posthog;

      if (ph?.persistence?.props?.$stored_person_properties?.email !== state.user.email) {
        ph?.identify(
          state.user.user_hash,
          { email: state.user.email },
        );
      }
    },
    signInError(state, action) {
      const errorCode = pathOr('', ['payload', 'response', 'status'], action);
      const errorMessage = ERROR_MESSAGES[errorCode] || null;

      state.authStatus = AUTH_STATUS_ERROR;
      state.errorMessage = errorMessage;
    },
    resetState(state) {
      Object.assign(state, defaultState());
    },
    setTokens(state, payload) {
      state.tokens = {
        ...state.tokens,
        ...payload.data,
      };
    },
    setErrorMessage: (state, message) => {
      state.errorMessage = message;
    },
    setErrorMessages: (state, messages) => {
      state.errorMessages = messages;
    },
    setDialogData: (state, { step }) => {
      state.dialogData = {
        ...state.dialogData,
        step,
      };
    },
    setEmailData: (state, { email }) => {
      state.dialogData.email = email;
    },
    openDialog: (state) => {
      state.dialogOpened = true;
    },
    closeDialog: (state) => {
      state.dialogOpened = false;
    },
    setLastSequenceNumber(state, number) {
      Object.assign(state.user, { last_invoice_number: number });
    },
  },

  actions: {
    setLoginEmail({ commit }, { email }) {
      commit('setEmailData', { email });
    },
    switchDialogStep({ commit }, { step, email }) {
      commit('setDialogData', { step });
      commit('setEmailData', { email });
    },
    setAuthFromWindow({ commit }) {
      if (window && window.vueAuth) {
        const { user } = window.vueAuth;

        if (user) {
          const payload = {
            data: {
              user,
            },
          };
          commit('signInSuccess', { payload });
        }
      } else {
        commit('resetState');
      }
    },
    signIn({ commit, getters }, {
      email, code,
    }) {
      return new Promise((resolve, reject) => {
        const onSuccess = ['config/fetchConfigData'];

        commit(
          'api/fetch',
          {
            url: '/users/sign_in',
            method: 'POST',
            body: {
              user: {
                email,
                password: code,
              },
            },
            headers: getters.getHeaders,
            promise: {
              resolve,
              reject,
            },
            onSuccess: [
              'auth/signInSuccess',
              ...onSuccess,
              {
                type: 'auth/signInSuccessFlash',
              },
            ],
            onError: ['auth/checkDomainRedirect', 'auth/signInError'],
          },
          { root: true },
        );
      });
    },
    requestResetPassword({ commit }, { email }) {
      commit(
        'api/fetch',
        {
          url: '/users/password',
          method: 'POST',
          body: {
            user: {
              email,
            },
          },
          onSuccess: [
            'auth/requestResetPasswordSuccess',
            {
              type: 'flashMessages/addMessage',
              kind: 'success',
              key: 'requestResetPasswordSuccess',
            },
          ],
          onError: [
            {
              type: 'flashMessages/addMessage',
              kind: 'error',
              key: 'requestResetPasswordError',
            },
          ],
        },
        { root: true },
      );
    },
    submitResetPassword({ commit }, { password, token }) {
      return new Promise((resolve, reject) => {
        commit(
          'api/fetch',
          {
            url: '/users/password',
            method: 'PUT',
            body: {
              user: {
                password,
                reset_password_token: token,
              },
            },
            promise: {
              resolve,
              reject,
            },
            onSuccess: [
              {
                type: 'flashMessages/addMessage',
                kind: 'success',
                key: 'submitPasswordSuccess',
              },
            ],
            onError: [
              {
                type: 'flashMessages/addMessage',
                kind: 'error',
                key: 'submitPasswordError',
              },
            ],
          },
          { root: true },
        );
      });
    },
    changePassword({ commit, getters }, { currentPassword, password }) {
      commit(
        'api/fetch',
        {
          url: '/current_user/password',
          method: 'PUT',
          body: {
            user: {
              current_password: currentPassword,
              password,
            },
          },
          headers: getters.getHeaders,
          onStart: ['auth/clearErrorMessages'],
          onSuccess: [
            'settingsForm/closeDialog',
            {
              type: 'flashMessages/addMessage',
              kind: 'success',
              key: 'passwordChangeSuccess',
            },
          ],
          onError: [
            'auth/formSubmitError',
            {
              type: 'flashMessages/addMessage',
              kind: 'error',
              key: 'passwordChangeError',
            },
          ],
        },
        { root: true },
      );
    },
    signOut({ commit, getters }) {
      return new Promise((resolve, reject) => {
        commit(
          'api/fetch',
          {
            url: '/users/sign_out',
            method: 'DELETE',
            headers: getters.getHeaders,
            promise: {
              resolve,
              reject,
            },
            onSuccess: [
              'auth/resetState',
              'flashMessages/resetState',
              'uiEffects/hideFooterInvoicesInfo',
              {
                type: 'flashMessages/addMessage',
                kind: 'success',
                key: 'successLogout',
              },
            ],
            onError: [],
          },
          { root: true },
        );
      });
    },
    checkDomainRedirect({ _ }, action) {
      if (action.payload.response.status === 401) {
        const redirectUrl = action.payload.response.data.errors.base[0].location;

        if (redirectUrl !== undefined) {
          window.location = redirectUrl;
        }
      }

      return false;
    },
    signInSuccessFlash({ getters, dispatch }) {
      dispatch(
        'flashMessages/addMessage',
        {
          kind: getters.tarifExpired ? 'error' : 'success',
          key: getters.tarifExpired ? 'successLoginButExpired' : 'successLogin',
        },
        { root: true },
      );
    },
    signInSuccess({ commit }, payload) {
      commit('signInSuccess', payload);
    },
    requestResetPasswordSuccess({ commit }) {
      commit('requestResetPasswordSuccess');
    },
    signInError({ commit }, payload) {
      commit('signInError', payload);
    },
    resetState({ commit, dispatch }) {
      dispatch('invoiceForm/resetState', {}, { root: true });
      commit('resetState');

      const ph = posthogInstance || window.posthog;
      ph?.reset();
    },
    setTokens({ commit }, { payload }) {
      commit('setTokens', payload);
    },
    formSubmitError: ({ commit }, action) => {
      commit('setErrorMessages', action.payload.response.data.errors);
    },
    clearErrorMessages: ({ commit }) => {
      commit('clearErrorMessages');
    },
    openDialog({ commit }) {
      commit('openDialog');
    },
    closeDialog({ commit }) {
      commit('closeDialog');
    },
    setLastSequenceNumber({ commit }, number) {
      commit('setLastSequenceNumber', number);
    },
  },
  getters: {
    getDateDiff: state => differenceInCalendarDays(parseISO(state.user.active_until), new Date()),
    getHoursDiff: state => differenceInHours(parseISO(state.user.active_until), new Date()),
    getSecondsDiff: state => differenceInSeconds(parseISO(state.user.active_until), new Date()),
    tarifExpired: (state, getters) => getters.getDateDiff <= 0
      && getters.getHoursDiff <= 0
      && getters.getSecondsDiff <= 0,
    currentPlanID: state => state.user.current_plan_id,
    currentPlanName: state => state.user.current_plan_name,
    lastPlanName: state => state.user.last_plan_name,
    lastPlanId: state => state.user.last_plan_id,
    userHasMiniPlan: state => state.user.current_plan_name === MINI_PLAN_NAME,
    userHasPremiumPlan: state => state.user.current_plan_name === PREMIUM_PLAN_NAME,
    userHasPlan: state => !!state.user.current_plan_name,
    getUserEmail: state => state.user.email,
    isAuthorized: state => state.authStatus === AUTH_STATUS_AUTHORIZED,
    userHash: state => state.user.user_hash,
    getAuthHeader: state => (state.authStatus === AUTH_STATUS_AUTHORIZED
      ? { Authorization: `${state.tokens.access}` }
      : {}),
    getHeaders: (_state, getters) => ({
      ...DEFAULT_HEADERS,
      ...getters.getAuthHeader,
    }),
    // TODO can remove getAuthHeader and update getHeaders
    // getHeaders: () => DEFAULT_HEADERS,
    isTrial: state => state.user['trial?'],
    isTrialWillExpire: (_state, getters) => getters.isTrial
      && getters.getDateDiff <= EXPIRE_SUBSCRIPTION_THRESHOLD_DAYS,
    isOwner: state => state.user.role === 'owner',
    isTrialExpired: (state, getters) => state.user['trial?'] && getters.tarifExpired,
    isTarifLifetime: state => state.user.active_until === 'unlimited_lifetime',
    userActiveUntil: state => state.user.active_until,
    hasFreeTarif: state => state.user['has_free_tarif?'] === true,
    showThankYouPage: state => state.user['show_thank_you_page?'] === true,
    getRemainingTimeWord(state, _getters, _rootState, rootGetters) {
      if (state.user.active_until !== null) {
        return formatDistance(parseISO(state.user.active_until), new Date(), {
          addSuffix: true,
          ...getFnsLocale(rootGetters['env/env'].language),
        });
      }
      return '';
    },
    isTarifExpired: (_state, getters) => !getters.isTrial && getters.tarifExpired,
    isTarifWillExpire: (_state, getters) => !getters.isTrial
      && getters.getDateDiff <= EXPIRE_SUBSCRIPTION_THRESHOLD_DAYS,
    showSubscriptionBar: (_state, getters) => (getters.isTrial
        || getters.isTrialExpired
        || getters.isTarifExpired
        || getters.isTarifWillExpire
        || getters.userHasMiniPlan)
      && getters.isAuthorized,
  },
};
